import { ChevronDown, Download, X } from "lucide-react";

import type { Organization } from "@/modules/dashboard/cards/types/organizations-api.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaDropdownMenu } from "@/shared/components/overlays/ColocaDropdownMenu";
import useTranslations from "@/shared/lang/useTranslations";

import {
  HISTORY_STATUS_OPTIONS,
  RESCUE_CORRIDOR_OPTIONS,
  RESCUE_STATUS_LABEL_KEYS,
} from "../constants/payoutRescue.constants";
import type { PayoutStatus } from "../services/payoutRescue.service";
import type { RescueFiltersState, RescueTab } from "../types/payoutRescue.types";

export interface RescueFiltersProps {
  tab: RescueTab;
  filters: RescueFiltersState;
  onSearchChange: (search: string) => void;
  onOrganizationChange: (organizationId: string | null) => void;
  onCountryChange: (country: string | null) => void;
  /** Status filter — history tab only (the queue tabs ARE a status each). */
  historyStatus: PayoutStatus | null;
  onHistoryStatusChange: (status: PayoutStatus | null) => void;
  onClear: () => void;
  hasActiveFilters: boolean;
  /** Org catalog for the filter menu (INTERNAL staff — `organizations:read`); empty while loading/denied. */
  organizations: Organization[];
  onExport: () => void;
  canExport: boolean;
}

/**
 * Toolbar of the rescue queue. Search is CLIENT-side (the runtime API has no free-text search) over the
 * loaded rows; org/corridor narrow the queue tabs client-side (they hold the full ≤200-row page) and the
 * history tab server-side. Export downloads the visible rows as CSV — there is no export endpoint.
 */
export function RescueFilters({
  tab,
  filters,
  onSearchChange,
  onOrganizationChange,
  onCountryChange,
  historyStatus,
  onHistoryStatusChange,
  onClear,
  hasActiveFilters,
  organizations,
  onExport,
  canExport,
}: RescueFiltersProps) {
  const { t } = useTranslations("payoutRescue");

  const statusLabel = (status: string) => {
    const key = RESCUE_STATUS_LABEL_KEYS[status];
    return key ? t(key) : status;
  };

  const orgItems = [
    { label: t("filter_all_orgs"), onClick: () => onOrganizationChange(null) },
    ...organizations.map((org) => ({
      label: org.name,
      onClick: () => onOrganizationChange(org.id),
    })),
  ];

  const corridorItems = [
    { label: t("filter_all_corridors"), onClick: () => onCountryChange(null) },
    ...RESCUE_CORRIDOR_OPTIONS.map((corridor) => ({
      label: `${corridor.country} · ${corridor.rail} · ${corridor.currency}`,
      onClick: () => onCountryChange(corridor.country),
    })),
  ];

  const statusItems = [
    { label: t("filter_all_statuses"), onClick: () => onHistoryStatusChange(null) },
    ...HISTORY_STATUS_OPTIONS.map((status) => ({
      label: statusLabel(status),
      onClick: () => onHistoryStatusChange(status),
    })),
  ];

  const activeOrg = organizations.find((org) => org.id === filters.organizationId);
  const activeCorridor = RESCUE_CORRIDOR_OPTIONS.find((c) => c.country === filters.country);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <ColocaSearchInput
        value={filters.search}
        onValueChange={onSearchChange}
        placeholder={t("filter_search_placeholder")}
        className="w-full max-w-xs"
      />
      {organizations.length > 0 && (
        <ColocaDropdownMenu
          trigger={
            <ColocaButton variant="default" size="sm" iconTrailing={ChevronDown}>
              {activeOrg?.name ?? t("filter_org_label")}
            </ColocaButton>
          }
          items={orgItems}
          align="start"
        />
      )}
      <ColocaDropdownMenu
        trigger={
          <ColocaButton variant="default" size="sm" iconTrailing={ChevronDown}>
            {activeCorridor
              ? `${activeCorridor.country} · ${activeCorridor.rail}`
              : t("filter_corridor_label")}
          </ColocaButton>
        }
        items={corridorItems}
        align="start"
      />
      {tab === "history" && (
        <ColocaDropdownMenu
          trigger={
            <ColocaButton variant="default" size="sm" iconTrailing={ChevronDown}>
              {historyStatus ? statusLabel(historyStatus) : t("filter_status_label")}
            </ColocaButton>
          }
          items={statusItems}
          align="start"
        />
      )}
      {hasActiveFilters && (
        <ColocaButton variant="ghost" size="sm" icon={X} onClick={onClear}>
          {t("filter_clear")}
        </ColocaButton>
      )}
      <div className="ml-auto">
        <ColocaButton variant="default" size="sm" icon={Download} onClick={onExport} disabled={!canExport}>
          {t("export_button")}
        </ColocaButton>
      </div>
    </div>
  );
}
